import { apiClient } from './api'
import type { ManualDraft, ManualPlanContext, ManualPlanRequest, ManualPlanValidation } from '../types/manualPlan'
import {
  OptimizationInput,
  OptimizationRunListParams,
  OptimizationRunListResponse,
  OptimizationRunResponse,
} from '../types/optimization'
import type { OptimizationOutputSummary, OptimizationExecutionDossier, OptimizationPlanPublication, OptimizationShareLink, SharedOptimizationPlan, OptimizationArtifact } from '../types/optimizationOutput'

const MISSING_TRACTOR_WARNING_TYPE = 'tractor_missing_for_semitrailer'

const isSemiTrailerWithoutTractor = (input: any): boolean => {
  const loadSpace = input?.loadSpace || input?.load_space
  const axleConfig = input?.vehicle?.axle_configuration || loadSpace?.axle_configuration || loadSpace?.axle_rules
  const physicsType = typeof axleConfig === 'object' ? axleConfig?.physicsType || axleConfig?.physics_type : null
  return (input?.vehicle?.type === 'SEMI_TRAILER' || physicsType === 'tractor_semitrailer') && !input?.tractor
}

const buildMissingTractorWarning = () => ({
  type: MISSING_TRACTOR_WARNING_TYPE,
  severity: 'warning',
  message: "Semi-remorque optimisée sans tracteur attelé : la répartition de charge sur les essieux du tracteur, la charge sellette et la validation de la combinaison complète n'ont pas été calculées.",
  recommendations: [
    "Sélectionner un tracteur compatible dans l'onglet Équipement pour valider la combinaison complète.",
    "Interpréter les charges essieux et le centre de gravité comme une estimation limitée à la semi-remorque.",
  ],
})

const withDerivedWarnings = <T extends OptimizationRunResponse>(response: T): T => {
  const run = response.run
  if (!run?.result || !isSemiTrailerWithoutTractor(run.input)) return response

  const warnings = run.result.warnings || []
  if (warnings.some((warning: any) => typeof warning === 'object' && warning.type === MISSING_TRACTOR_WARNING_TYPE)) {
    return response
  }

  return {
    ...response,
    run: {
      ...run,
      result: {
        ...run.result,
        warnings: [
          ...warnings,
          buildMissingTractorWarning(),
        ],
      },
    },
  }
}

export const optimizationApi = {
  getManualPlan: async (id: string) => (await apiClient.get<ManualPlanContext>(`/optimization/v1/runs/${encodeURIComponent(id)}/manual-plan`)).data,
  validateManualPlan: async (id: string, body: ManualPlanRequest) => (await apiClient.post<ManualPlanValidation>(`/optimization/v1/runs/${encodeURIComponent(id)}/manual-plan/validate`, body)).data,
  saveManualPlan: async (id: string, body: ManualPlanRequest) => (await apiClient.post<OptimizationRunResponse>(`/optimization/v1/runs/${encodeURIComponent(id)}/manual-plan`, body)).data,
  saveManualDraft: async (id: string, body: ManualPlanRequest & { draftVersion: number }) => (await apiClient.post<{ draft: ManualDraft; validation: ManualPlanValidation }>(`/optimization/v1/runs/${encodeURIComponent(id)}/manual-plan/draft`, body)).data,
  /**
   * List optimization runs with optional status filter and pagination
   */
  listRuns: async (params: OptimizationRunListParams = {}) => {
    const query: Record<string, string | number> = {}
    if (params.status) query.status = params.status
    if (params.limit != null) query.limit = params.limit
    if (params.offset != null) query.offset = params.offset

    const response = await apiClient.get<OptimizationRunListResponse>(
      '/optimization/v1/runs',
      { params: query }
    )
    return response.data
  },

  /**
   * Get details of a specific optimization run
   */
  getRunDetail: async (runId: string): Promise<OptimizationRunResponse> => {
    const response = await apiClient.get<OptimizationRunResponse>(
      `/optimization/v1/runs/${runId}`,
      { headers: { 'Cache-Control': 'no-cache' } }
    )
    return withDerivedWarnings(response.data)
  },

  getOutputSummary: async (runId: string): Promise<OptimizationOutputSummary> => {
    const response = await apiClient.get<{ outputSummary: OptimizationOutputSummary }>(
      `/optimization/v1/runs/${runId}/output-summary`
    )
    return response.data.outputSummary
  },

  getExecutionDossier: async (runId: string): Promise<OptimizationExecutionDossier> => {
    const response = await apiClient.get<{ executionDossier: OptimizationExecutionDossier }>(
      `/optimization/v1/runs/${encodeURIComponent(runId)}/execution-dossier`
    )
    return response.data.executionDossier
  },

  getPublication: async (runId: string): Promise<OptimizationPlanPublication | null> => {
    const response = await apiClient.get<{ publication: OptimizationPlanPublication | null }>(
      `/optimization/v1/runs/${encodeURIComponent(runId)}/publication`
    )
    return response.data.publication
  },

  requestPublicationReview: async (runId: string, comment?: string | null): Promise<OptimizationPlanPublication> => {
    const response = await apiClient.post<{ publication: OptimizationPlanPublication }>(
      `/optimization/v1/runs/${encodeURIComponent(runId)}/review`, { comment: comment || undefined }
    )
    return response.data.publication
  },

  approvePublication: async (runId: string, publicationId: string, exceptionReason?: string | null): Promise<OptimizationPlanPublication> => {
    const response = await apiClient.post<{ publication: OptimizationPlanPublication }>(
      `/optimization/v1/runs/${encodeURIComponent(runId)}/approve`,
      { publication_id: publicationId, exception_reason: exceptionReason || undefined }
    )
    return response.data.publication
  },

  publishPublication: async (runId: string, publicationId: string, exceptionReason?: string | null): Promise<OptimizationPlanPublication> => {
    const response = await apiClient.post<{ publication: OptimizationPlanPublication }>(
      `/optimization/v1/runs/${encodeURIComponent(runId)}/publish`,
      { publication_id: publicationId, exception_reason: exceptionReason || undefined }
    )
    return response.data.publication
  },

  listShareLinks: async (runId: string): Promise<OptimizationShareLink[]> => {
    const response = await apiClient.get<{ share_links: OptimizationShareLink[] }>(`/optimization/v1/runs/${encodeURIComponent(runId)}/share-links`)
    return response.data.share_links
  },

  createShareLink: async (runId: string, payload: { publicationId: string; scope: OptimizationShareLink['scope']; expiresAt: string; allowDownload?: boolean }): Promise<OptimizationShareLink> => {
    const response = await apiClient.post<{ share_link: OptimizationShareLink }>(`/optimization/v1/runs/${encodeURIComponent(runId)}/share-links`, {
      publication_id: payload.publicationId, scope: payload.scope, expires_at: payload.expiresAt,
      allow_download: payload.allowDownload === true, public_base_url: window.location.origin
    })
    return response.data.share_link
  },

  revokeShareLink: async (runId: string, shareId: string): Promise<void> => {
    await apiClient.delete(`/optimization/v1/runs/${encodeURIComponent(runId)}/share-links/${encodeURIComponent(shareId)}`)
  },

  getSharedPlan: async (token: string): Promise<SharedOptimizationPlan> => {
    const response = await apiClient.get<SharedOptimizationPlan>(`/optimization/v1/shared/${encodeURIComponent(token)}`)
    return response.data
  },

  createQuayArtifact: async (runId: string, publicationId: string): Promise<OptimizationArtifact> => {
    const response = await apiClient.post<{ artifact: OptimizationArtifact }>(`/optimization/v1/runs/${encodeURIComponent(runId)}/artifacts`, { type: 'quay_pdf', publication_id: publicationId })
    return response.data.artifact
  },

  listArtifacts: async (runId: string): Promise<OptimizationArtifact[]> => {
    const response = await apiClient.get<{ artifacts: OptimizationArtifact[] }>(`/optimization/v1/runs/${encodeURIComponent(runId)}/artifacts`)
    return response.data.artifacts
  },

  downloadArtifact: async (runId: string, artifactId: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(
      `/optimization/v1/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifactId)}/download`,
      { responseType: 'blob' }
    )
    return response.data
  },

  /**
   * Create a new optimization run
   */
  createRun: async (input: OptimizationInput) => {
    const response = await apiClient.post<OptimizationRunResponse>(
      '/optimization/v1/runs',
      input
    )
    return withDerivedWarnings(response.data)
  },

  /**
   * Cancel an ongoing optimization
   */
  cancelRun: async (runId: string) => {
    const response = await apiClient.post<OptimizationRunResponse>(
      `/optimization/v1/runs/${runId}/cancel`
    )
    return response.data
  },

  /**
   * Export optimization results
   */
   exportRun: async (runId: string, format: 'json' | 'csv' | 'xlsx' | 'pdf', template?: 'summary' | 'manifest' | 'mass_axle_cg' | 'exceptions' | 'quay', presentation?: { locale: 'fr' | 'en'; units: 'metric' | 'imperial' }): Promise<Blob> => {
     // An export must come from the persisted, tenant-scoped run.
    const response = await apiClient.get<Blob>(
      `/optimization/v1/runs/${encodeURIComponent(runId)}/export`,
      { params: { format, ...(format === 'pdf' ? { template, ...presentation } : {}) }, responseType: 'blob' }
    )
    const expected = { json: 'application/json', csv: 'text/csv', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', pdf: 'application/pdf' }[format]
    const contentType = response.headers['content-type'] || response.data.type
    if (!(response.data instanceof Blob) || !String(contentType).toLowerCase().includes(expected)) {
      throw new Error('Le serveur n’a pas retourné un fichier d’export valide.')
    }
    return response.data
  },

  /**
   * Replan an optimization run using the dedicated /replan endpoint
   * This leverages backend diff logic, causation_id, and plan_version tracking
   */
  replanRun: async (runId: string, replanData: {
    previousRun?: any
    newScenario?: any
    input?: any
    options?: any
  }) => {
    const response = await apiClient.post<OptimizationRunResponse>(
      `/optimization/v1/runs/${runId}/replan`,
      replanData
    )
    return response.data
  },
}
